import { GridColumnOrderChangeParams, GridPinnedColumnFields } from "@mui/x-data-grid";
import { DEFAULT_VIEW_NAME, ROUTES } from "../../../utils/constants";
import { LayoutPage } from "../../layoutPage";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { SxProps, Theme, ThemeProvider } from "@mui/material/styles";
import { useMemo, useState, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { getAuth } from "../../../redux/auth/authSlice";
import { GridCellParams } from "@mui/x-data-grid-premium";
import { IColumnVisibilityModel, PREF_TASKS } from "../../../api/concierges/preferences";
import Preferences from "../../../components/preferences/prefereneces";
import { CustomToolbar, theme } from "../../../components/gridToolbarComponents/gridToolbarComponents";
import { useHomeSiteObserver } from "../../../reactQuery/homeSites/homsites/homeSiteQuery";
import {
  useHomeSiteTasksObserver,
  useMileStoneTasksData,
  useMileStoneTasksIncrementalData,
} from "../../../reactQuery/homeSites/milestones/mileStoneTasksQuery";

import TaskFilters from "./taskFilter";
import { HomesiteColDef, TableColumnMap, useHomesite } from "../../../components/core/homesites/homesiteColumns";
import { useLocation } from "react-router-dom";
import {
  getMilestoneCounts,
  getHomesiteMilestoneRows,
  MileStoneCount,
} from "../../../utils/homeSiteTaskMetrics/homeSiteTaskMetrics";
import HomeSiteFullDetails from "../homeSiteDetailsModal/HomeSiteFullDetails";
import { useAlertsData } from "../../../reactQuery/homeSites/alerts/homesiteAlerts";
import { useExceptionsData } from "../../../reactQuery/homeSites/exceptions/homeSiteExceptions";
import { useNotesData } from "../../../reactQuery/homeSites/notes/homeSiteNotes";
import { ALL_MILESTONES, THIS_WEEK } from "../dashboard/taskMetricsComponent";
import { getDataGridRowStyles } from "../../../components/datagrid/datagridStyles"
import { defaultNonVisibleColumnsArray, syncRevisedEcoeDelayedReasonVisibility } from "./tasksUtils";
import { useDivisionMileStoneDataReleased } from "../../../reactQuery/division/divisionMileStoneQuery";
import { DivisionMileStone, DivisionMileStones } from "../../../api/divisionMileStones/types";
import { HomeSitesDataIndex } from "../../../reactQuery/homeSitesDataIndex/types";
import { useQueryClient } from "@tanstack/react-query";
import { pinnedSessionKey, sanitizePinned, selectedViewSessionKey } from "../../../utils/pinning";
import { useHomeSiteDataIndexQuery } from "../../../reactQuery/homeSitesDataIndex/homeSiteDataIndexQuery";
import { useBuyerListingData } from "../buyerListing/useBuyerListingData";
import { useDivisionFeatureFlags } from "../../../stateContext/featureFlags/FeatureFlagProvider";

export const Tasks = () => {
  const location = useLocation();
  const { timeFrame: timeFrameSelect = THIS_WEEK, milestone = ALL_MILESTONES } = location.state || {};

  const { userInfo } = useSelector(getAuth);
  const { getSortedColumns } = useHomesite();
  const { showMilestoneProgress } = useDivisionFeatureFlags(userInfo?.userDivCode || "");

  const taskColumns = useMemo(
    () =>
      getSortedColumns(
        true,
        ["homeSiteId", "milestoneName", "taskName", "taskDueDate",
          "numExceptions", "numNotes", "numAlerts", "milestoneProgress", "contactInfo"],
        TableColumnMap.TaskFields
      ),
    []
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<IColumnVisibilityModel>({});
  const [orderedColumns, setOrderedColumns] = useState<string[]>([]);
  const [columnReordered, setColumnReordered] = useState(false);

  const defaultPinnedTasks = (): GridPinnedColumnFields =>
    sanitizePinned({ left: ["homeSiteId", "milestoneName"], right: [] }, showMilestoneProgress);
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>(defaultPinnedTasks());

  // Data Index Query
  const { data: homeSitesDataIndex } = useHomeSiteDataIndexQuery(userInfo.userDivCode);
  const { applyDynamicFilters, filteredHomesites } = useHomeSiteObserver(homeSitesDataIndex || []);
  const { data: milestones } = useMileStoneTasksData(userInfo.userDivCode);
  const { data: notes } = useNotesData(userInfo.userDivCode);
  const { data: exceptions } = useExceptionsData(userInfo.userDivCode);
  const { data: alerts } = useAlertsData(userInfo.userDivCode);
  const { data: divisionMilestonesData } = useDivisionMileStoneDataReleased(userInfo.userDivCode);

  const [selectedDropdownMilestone, setSelectedDropdownMilestone] = useState(milestone);
  const [timeFrame, setTimeFrame] = useState(timeFrameSelect);

  const { isLoading: msLoading } = useMileStoneTasksIncrementalData(userInfo.userDivCode, milestones?.continuationToken ?? "");
  const { metrics: taskMetrics } = useHomeSiteTasksObserver(filteredHomesites || [], milestones?.data || [], divisionMilestonesData || new DivisionMileStones("0", []), msLoading);
  const { selectedViewFromBuyerListing } = useBuyerListingData();

  const uniqueMilestoneNames = useMemo(() => {
    if (!milestones) return [];
    // Use Set for O(1) lookup instead of O(n) includes()
    const uniqueMilestonesSet = new Set<string>();
    
    // Add division milestones first
    if (divisionMilestonesData?.data) {
      for (const milestone of divisionMilestonesData.data) {
        uniqueMilestonesSet.add(milestone.mileStoneName);
      }
    }
    
    // Add milestone tasks
    if (milestones?.data) {
      for (const milestone of milestones.data) {
        uniqueMilestonesSet.add(milestone.mileStoneName);
      }
    }
    
    return Array.from(uniqueMilestonesSet);
  }, [milestones, divisionMilestonesData]);

  const milestoneCounts: Record<string, MileStoneCount> = useMemo(() => {
    return getMilestoneCounts(taskMetrics, timeFrame, divisionMilestonesData);
  }, [taskMetrics, timeFrame, divisionMilestonesData]);

  const homesiteMilestoneRows = useMemo(() => {
    if (!filteredHomesites || !taskMetrics || !notes || !exceptions || !alerts) return [];
    return getHomesiteMilestoneRows(
      filteredHomesites as HomeSitesDataIndex[],
      taskMetrics,
      timeFrame,
      selectedDropdownMilestone,
      notes,
      exceptions,
      alerts,
      divisionMilestonesData
    );
  }, [alerts, exceptions, filteredHomesites, timeFrame, selectedDropdownMilestone, taskMetrics, notes, divisionMilestonesData]);

  const handleColumnVisibilityChange = useCallback((newModel: IColumnVisibilityModel) => {
    setColumnVisibilityModel((previousModel) =>
      syncRevisedEcoeDelayedReasonVisibility(newModel, previousModel)
    );
  }, []);

  const [informationModal, setInformationModal] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [optionalMilestone, setOptionalMilestone] = useState<string | undefined>(undefined);
  const [clickedField, setClickedField] = useState<string | null>(null);

  const handleColumnOrderChange = useCallback(
    (params: GridColumnOrderChangeParams) => {
      const { oldIndex, targetIndex } = params;
      const left = pinnedColumns.left ?? [];
      const right = pinnedColumns.right ?? [];

      const allFieldsBase = taskColumns.map(c => c.field);

      // Rebuild full order if orderedColumns is empty or stale
      let currentFullOrder: string[];
      if (
        orderedColumns.length === 0 ||
        orderedColumns.length !== allFieldsBase.length
      ) {
        const leftSet = new Set(left);
        const rightSet = new Set(right);
        const unpinned = allFieldsBase.filter(f => !leftSet.has(f) && !rightSet.has(f));
        currentFullOrder = [...left, ...unpinned, ...right];
      } else {
        // Use Set for O(1) lookup instead of O(n) includes()
        const orderedColumnsSet = new Set(orderedColumns);
        const allFieldsInOrdered = allFieldsBase.every(f => orderedColumnsSet.has(f));
        if (!allFieldsInOrdered) {
          const leftSet = new Set(left);
          const rightSet = new Set(right);
          const unpinned = allFieldsBase.filter(f => !leftSet.has(f) && !rightSet.has(f));
          currentFullOrder = [...left, ...unpinned, ...right];
        } else {
          currentFullOrder = [...orderedColumns];
        }
      }

      const leftCount = left.length;
      const rightCount = right.length;
      const unpinnedCount = currentFullOrder.length - leftCount - rightCount;
      const lastUnpinnedIndex = leftCount + unpinnedCount - 1;

      const dragInUnpinned = oldIndex >= leftCount && oldIndex <= lastUnpinnedIndex;
      const dropInUnpinned = targetIndex >= leftCount && targetIndex <= lastUnpinnedIndex;
      if (!dragInUnpinned || !dropInUnpinned) return; // Ignore drags involving pinned columns

      const unpinnedSlice = currentFullOrder.slice(leftCount, leftCount + unpinnedCount);
      const from = oldIndex - leftCount;
      const to = targetIndex - leftCount;
      const moving = unpinnedSlice.splice(from, 1)[0];
      unpinnedSlice.splice(to, 0, moving);

      const newFull = [...left, ...unpinnedSlice, ...right];
      setOrderedColumns(newFull);
      setColumnReordered(true);
    },
    [orderedColumns, taskColumns, pinnedColumns]
  );

  const handleCellClick = useCallback((params: GridCellParams) => {
    if (params.field === "milestoneName" || params.field === "homeSiteId" || params.field === "numExceptions" || params.field === "numAlerts" || params.field === "numNotes") {
      setInformationModal(params.row);
      setClickedField(params.field);
      setShowModal(true);
      if (params.field === "milestoneName") {
        const milestone = params.row.mileStoneName;
        setOptionalMilestone(milestone);
      }
    }
  }, []);


  useEffect(() => {
  const selectedView = sessionStorage.getItem(selectedViewSessionKey(PREF_TASKS)) ?? DEFAULT_VIEW_NAME;
  const key = pinnedSessionKey(PREF_TASKS, selectedView);
  const fromSession = sessionStorage.getItem(key);
  if (fromSession) {
    try {
      setPinnedColumns(sanitizePinned(JSON.parse(fromSession), showMilestoneProgress));
    } catch (err) {
      console.error("Error parsing pinned columns from sessionStorage:", err);
    }
  }
}, [showMilestoneProgress]);

const handlePinnedColumnsChange = (next: GridPinnedColumnFields) => {
  const sanitized = sanitizePinned(next, showMilestoneProgress);
  setPinnedColumns(sanitized);

  const selectedView = sessionStorage.getItem(selectedViewSessionKey(PREF_TASKS)) ?? DEFAULT_VIEW_NAME;
  const key = pinnedSessionKey(PREF_TASKS, selectedView);
  sessionStorage.setItem(key, JSON.stringify(sanitized));
};

  return (
    <ThemeProvider theme={theme}>
      <LayoutPage title={ROUTES.closings.tasks.label}>
        <Box sx={styles.pageContainer}>
          <Box sx={styles.preferencesContainer}>
            <TaskFilters
              selectedDropdownMilestone={selectedDropdownMilestone}
              setSelectedDropdownMilestone={setSelectedDropdownMilestone}
              timeFrame={timeFrame}
              setTimeFrame={setTimeFrame}
              milestoneCounts={milestoneCounts}
              milestoneOrder={uniqueMilestoneNames}
              applyDynamicFilters={applyDynamicFilters}
            />
            <Preferences
              page={PREF_TASKS}
              email={userInfo?.email}
              columns={taskColumns}
              orderedColumns={orderedColumns}
              columnReordered={columnReordered}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              setOrderedColumns={setOrderedColumns}
              defaultNonVisibleColumns={defaultNonVisibleColumnsArray}
              selectedViewFromBuyerListing={selectedViewFromBuyerListing}
              pinnedColumns={pinnedColumns}
              setPinnedColumns={setPinnedColumns}
            />
          </Box>
          <Box sx={styles.dataGridBox}>
            <DataGridPremium
              columns={
                orderedColumns.length > 0
                  ? (() => {
                      // Build lookup map for O(1) access instead of O(n) find()
                      const columnsByField = new Map<string, HomesiteColDef>();
                      for (const col of taskColumns) {
                        columnsByField.set(col.field, col);
                      }
                      return orderedColumns
                        .map((field) => columnsByField.get(field))
                        .filter((col): col is HomesiteColDef => col !== undefined);
                    })()
                  : taskColumns
              }
              rows={homesiteMilestoneRows}
              rowHeight={35} /* By default row height is 52px */
              pagination
              disableColumnPinning={false}
              pinnedColumns={pinnedColumns}
              onPinnedColumnsChange={handlePinnedColumnsChange}
              disableAggregation
              disableRowGrouping
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25, page: 0 },
                },
                sorting: {
                  sortModel: [{ field: "taskDueDate", sort: "asc" }],
                },
              }}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityChange}
              onColumnOrderChange={handleColumnOrderChange}
              slots={{ toolbar: () => CustomToolbar(true, true, false) }}
              sx={styles.dataGrid as SxProps<Theme>}
              onCellClick={handleCellClick}
              getRowClassName={(params) => {
                return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
              }}
            />
          </Box>
        </Box>
      </LayoutPage>
      {showModal && informationModal && (
        <HomeSiteFullDetails
          userId={userInfo?.email}
          title={informationModal?.buyerName || ""}
          setShowModal={setShowModal}
          setInformationModal={setInformationModal}
          homeSiteDetails={informationModal}
          tab={{
            milestoneName: "basicdetails",
            homeSiteId: "additional",
            numExceptions: "exceptions",
            numAlerts: "alerts",
            numNotes: "notes"
          }[clickedField as string] || "basicdetails"}
          milestoneFilter={optionalMilestone}
        />
      )}
    </ThemeProvider>
  );
};

export const styles = {
  pageContainer: {
    width: "100%",
    height: "100%",
    backgroundColor: "#fff",
    padding: "16px",
    borderRadius: "8px",
  },
  filterContainer: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    marginBottom: "16px",
  },
  formControl: {
    width: "265px",
    marginLeft: "6px",
  },
  select: {
    height: "42px",
    backgroundColor: "#FAFBFC",
    borderBottom: "1px solid #1C2B36",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  boldText: {
    fontWeight: "bold",
    marginLeft: "4px",
  },
  preferencesContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  dataGridBox: {
    height: "75vh",
    width: "100%",
    "& .super-app-theme--header": {
      backgroundColor: "#DCE3E8",
    },
  },
  dataGrid: {
    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
    "& .MuiDataGrid-columnSeparator": {
      visibility: "visible",
      color: "divider",
    },
    "& .MuiDataGrid-cell": {
      borderRight: "1px solid rgba(224, 224, 224, 1)",
    },
    "& .super-app-theme--header": {
      backgroundColor: "#DCE3E8",
    },
    ...getDataGridRowStyles(theme, true),
  },
};
